import { getPasskey, type LinkStatus, type SecondFactor } from '@central/api';
import { color, isApiError } from '@central/ui';
import { Alert, Button, Loader, PasswordInput, TextInput } from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../api';
import { errorMessage } from '../format';
import { useSignedIn } from '../signedIn';
import { AuthLayout, FormFrame } from './SignIn';

const noFactor =
  'Your account has no second factor yet. Sign in with your password, add a passkey or an authenticator app, then sign in through single sign-on again.';

/** link_expired is answered by the expired screen, not an inline alert. */
const expiredError = (e: unknown) => isApiError(e, 'link_expired');

const body = { margin: 0, fontSize: 14, lineHeight: '21px', color: color.textBody } as const;

/**
 * Step-up link (`/sign-in/link`): the first SSO sign-in for an existing user
 * proves the account with its password and a second factor before the SSO
 * identity is linked to it. The pending link lives in the server's
 * __Host-central_sso cookie; GET /auth/link reports how far it got.
 */
export function LinkSso() {
  const status = useQuery({
    queryKey: ['auth', 'link'],
    queryFn: () => api.auth.link.status(),
    retry: false,
    staleTime: Infinity,
  });
  return (
    <AuthLayout>
      {status.isPending ? (
        <Loader size="sm" aria-label="Loading" />
      ) : status.error ? (
        isApiError(status.error, 'link_expired') ? (
          <Expired />
        ) : (
          <FormFrame title="Link your single sign-on">
            <Alert color="red">{errorMessage(status.error)}</Alert>
          </FormFrame>
        )
      ) : (
        <Steps link={status.data} />
      )}
    </AuthLayout>
  );
}

function Expired() {
  return (
    <FormFrame title="This sign-on link has expired">
      <p style={body}>
        Linking has to finish within 10 minutes, in the browser that started it. Sign in through
        your organization’s SSO again to start over.
      </p>
      <Link to="/sign-in" style={{ color: color.link, fontSize: 14 }}>
        Back to sign in
      </Link>
    </FormFrame>
  );
}

function Steps({ link }: { link: LinkStatus }) {
  const [methods, setMethods] = useState<SecondFactor[] | undefined>(
    link.passwordVerified ? link.methods : undefined,
  );
  const [expired, setExpired] = useState(false);
  if (expired) return <Expired />;
  const onError = (e: unknown) => {
    if (isApiError(e, 'link_expired')) setExpired(true);
    else if (isApiError(e, 'password_required')) setMethods(undefined);
  };

  return (
    <FormFrame title="Link your single sign-on">
      <p style={body}>
        You signed in through {link.provider} as <b>{link.email}</b> for the first time. Confirm
        it’s your GoATMS Central account once; after that, {link.provider} signs you straight in.
      </p>
      {methods === undefined ? (
        <PasswordStep onVerified={setMethods} onError={onError} />
      ) : (
        <FactorStep methods={methods} onError={onError} />
      )}
    </FormFrame>
  );
}

function PasswordStep({
  onVerified,
  onError,
}: {
  onVerified: (m: SecondFactor[]) => void;
  onError: (e: unknown) => void;
}) {
  const [password, setPassword] = useState('');
  const verify = useMutation({
    mutationFn: () => api.auth.link.password({ password }),
    onSuccess: (r) => onVerified(r.methods),
    onError: (e) => onError(e),
  });
  const err = verify.error;
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        verify.mutate();
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
    >
      <PasswordInput
        label="Password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.currentTarget.value)}
        required
      />
      {err && !expiredError(err) && (
        <Alert color="red">
          {isApiError(err, 'second_factor_required') ? noFactor : errorMessage(err)}
        </Alert>
      )}
      <Button type="submit" fullWidth loading={verify.isPending}>
        Continue
      </Button>
    </form>
  );
}

function FactorStep({
  methods,
  onError,
}: {
  methods: SecondFactor[];
  onError: (e: unknown) => void;
}) {
  const signedIn = useSignedIn();
  const [code, setCode] = useState('');
  const totp = useMutation({
    mutationFn: () => api.auth.link.totp({ code: code.trim() }),
    onSuccess: signedIn,
    onError: (e) => onError(e),
  });
  const passkey = useMutation({
    mutationFn: async () => {
      const { publicKey } = await api.auth.link.webauthnBegin();
      const credential = await getPasskey(publicKey);
      return api.auth.link.webauthnFinish({ credential });
    },
    onSuccess: signedIn,
    onError: (e) => onError(e),
  });
  const error = totp.error ?? passkey.error;
  if (methods.length === 0) return <Alert color="red">{noFactor}</Alert>;
  return (
    <>
      {methods.includes('webauthn') && (
        <Button fullWidth loading={passkey.isPending} onClick={() => passkey.mutate()}>
          Use a passkey
        </Button>
      )}
      {methods.includes('totp') && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            totp.mutate();
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
        >
          <TextInput
            label="Code from your authenticator app"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
            required
          />
          <Button
            type="submit"
            fullWidth
            variant={methods.includes('webauthn') ? 'default' : 'filled'}
            loading={totp.isPending}
          >
            Verify and link
          </Button>
        </form>
      )}
      {error && !expiredError(error) && <Alert color="red">{errorMessage(error)}</Alert>}
    </>
  );
}
